/** @jsx jsx */

import { jsx, css } from '@emotion/core';

import { Colors, Spacer } from '@zapier/design-system';
import { Spacing } from '@zapier/theme';

import LoadingBlock from 'app/common/components/LoadingBlock';
import { Status, ZapTemplate } from 'app/zap-templates/types';

import Header from '../Header';
import NoTemplates from '../NoTemplates';
import Sidebar from '../Sidebar';
import ZapTemplateItem from '../ZapTemplateItem';

const Styles = {
  body: css`
    flex: 1;
  `,
  paginator: css`
    display: flex;
    justify-content: center;
  `,
  root: css`
    background: ${Colors.PrimeWhite};
    overflow: auto;
  `,
  sidebar: css`
    flex: 0 1 auto;
    width: 290px;
  `,
  wrapper: css`
    display: flex;
    gap: ${Spacing.large}px;
    height: calc(100vh - 70px);
    margin: 0 auto;
    max-width: 1120px;
    padding: ${Spacing.medium}px;
  `,
};

type Props = {
  isLoading: boolean;
  onChangeStatus: (status: Status | undefined) => void;
  status: Status | undefined;
  zapTemplates: ZapTemplate[];
};

function ZapTemplates({
  isLoading,
  onChangeStatus,
  status,
  zapTemplates,
}: Props) {
  // Separate community templates from other templates
  const communityTemplates = zapTemplates.filter(
    template => template.status === Status.community
  );
  const nonCommunityTemplates = zapTemplates.filter(
    template => template.status !== Status.community
  );

  // Filter based on selected status, but only for non-community templates
  const filteredTemplates = status
    ? nonCommunityTemplates.filter(template => template.status === status)
    : nonCommunityTemplates;

  return (
    <div css={Styles.root}>
      <div css={Styles.wrapper}>
        <div css={Styles.sidebar}>
          <Sidebar
            onChangeStatus={onChangeStatus}
            status={status}
            zapTemplates={nonCommunityTemplates}
          />
        </div>
        <main css={Styles.body}>
          <Header status={status} />
          <Spacer height="30px" />
          {!!filteredTemplates.length && (
            <ul>
              {filteredTemplates.map(zapTemplate => (
                <ZapTemplateItem
                  key={zapTemplate.id}
                  zapTemplate={zapTemplate}
                />
              ))}
            </ul>
          )}
          {isLoading && <LoadingBlock />}
          {!isLoading && !filteredTemplates.length && (
            <NoTemplates status={status} />
          )}

          {/* Community Templates Section */}
          {communityTemplates.length > 0 && (
            <div>
              <Spacer height="40px" />
              <Header status={Status.community} />
              <ul>
                {communityTemplates.map(zapTemplate => (
                  <ZapTemplateItem
                    key={zapTemplate.id}
                    zapTemplate={zapTemplate}
                  />
                ))}
              </ul>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default ZapTemplates;
